import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDateiDrop } from '../lib/useDateiDrop';
import './ChatInput.css';

const MAX_DATEI_GROESSE = 5 * 1024 * 1024; // 5 MB
const DATEI_ACCEPT = 'image/*,application/pdf,text/*,.eml,message/rfc822,.msg,application/vnd.ms-outlook,.ugl,.csv,.xlsx,.xls,.xml,application/xml,text/xml';

export interface Attachment {
  type: 'image' | 'file' | 'audio';
  name: string;
  mimeType: string;
  base64: string;
  previewUrl?: string;
}

interface ChatInputProps {
  onSendMessage: (message: string, attachments: Attachment[]) => void;
  disabled?: boolean;
  /** Solange der Agent arbeitet, wird der Senden-Button zum Stop-Button */
  onAbort?: () => void;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/* Entwurf überlebt das Unmounten des Chat-Zweigs (Wechsel in Verwaltung/
   SuperAdmin) — sessionStorage statt Lift-up, damit er auch einen Reload
   des Tabs übersteht. */
const ENTWURF_KEY = 'chat_eingabe_entwurf';

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, disabled, onAbort }) => {
  const [input, setInput] = useState(() => {
    try {
      return sessionStorage.getItem(ENTWURF_KEY) || '';
    } catch {
      return '';
    }
  });
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [recording, setRecording] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);

  // Wire camera stream to video element when modal opens
  useEffect(() => {
    if (showCamera && videoRef.current && cameraStreamRef.current) {
      videoRef.current.srcObject = cameraStreamRef.current;
      videoRef.current.play().catch(() => {/* autoplay policy may block — user gesture already happened */});
    }
  }, [showCamera]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if ((input.trim() || attachments.length > 0) && !disabled) {
      onSendMessage(input, attachments);
      setInput('');
      try { sessionStorage.removeItem(ENTWURF_KEY); } catch {}
      setAttachments([]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // ── Datei-/Bild-Upload ──────────────────────────────────────────────
  const verarbeiteDateien = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    const newAttachments: Attachment[] = [];

    for (const file of files.slice(0, 3)) {
      if (file.size > MAX_DATEI_GROESSE) {
        alert(`${file.name} ist größer als 5 MB und wird übersprungen.`);
        continue;
      }
      const dataUrl = await blobToBase64(file);
      const base64 = dataUrl.split(',')[1];
      const isImage = file.type.startsWith('image/');
      newAttachments.push({
        type: isImage ? 'image' : 'file',
        name: file.name,
        mimeType: file.type || 'application/octet-stream',
        base64,
        previewUrl: isImage ? dataUrl : undefined,
      });
    }

    setAttachments(prev => [...prev, ...newAttachments].slice(0, 3));
  }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    await verarbeiteDateien(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Drag-and-Drop ────────────────────────────────────────────────────
  const { dragOver, dropHandlers } = useDateiDrop({
    accept: DATEI_ACCEPT,
    multiple: true,
    onFiles: dateien => { void verarbeiteDateien(dateien); },
    onError: fehler => alert(fehler),
  });

  // ── Mikrofon-Aufnahme ───────────────────────────────────────────────
  const handleMicClick = async () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const recorder = new MediaRecorder(stream);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const dataUrl = await blobToBase64(blob);
        setAttachments(prev => [
          ...prev,
          {
            type: 'audio' as const,
            name: `aufnahme-${Date.now()}.webm`,
            mimeType: 'audio/webm',
            base64: dataUrl.split(',')[1],
          },
        ].slice(0, 3));
        stream.getTracks().forEach(t => t.stop());
        setRecording(false);
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch {
      alert('Mikrofon-Zugriff wurde verweigert oder ist nicht verfügbar.');
    }
  };

  // ── Kamera ─────────────────────────────────────────────────────────
  const handleCameraOpen = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      cameraStreamRef.current = stream;
      setShowCamera(true);
    } catch {
      alert('Kamera-Zugriff wurde verweigert oder ist nicht verfügbar.');
    }
  };

  const handleCapture = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d')!.drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setAttachments(prev => [
      ...prev,
      {
        type: 'image' as const,
        name: `kamera-${Date.now()}.jpg`,
        mimeType: 'image/jpeg',
        base64: dataUrl.split(',')[1],
        previewUrl: dataUrl,
      },
    ].slice(0, 3));
    handleCameraClose();
  };

  const handleCameraClose = () => {
    cameraStreamRef.current?.getTracks().forEach(t => t.stop());
    cameraStreamRef.current = null;
    setShowCamera(false);
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className={`chat-input-wrapper ${dragOver ? 'chat-input-wrapper--drag-over' : ''}`} {...dropHandlers}>
      {dragOver && (
        <div className="chat-drop-overlay">Datei hier ablegen</div>
      )}
      {/* Kamera-Modal */}
      {showCamera && (
        <div className="camera-modal-overlay" onClick={handleCameraClose}>
          <div className="camera-modal" onClick={e => e.stopPropagation()}>
            <video ref={videoRef} className="camera-preview" autoPlay muted playsInline />
            <div className="camera-controls">
              <button className="k-btn k-btn--primary" onClick={handleCapture}>Aufnehmen</button>
              <button className="k-btn k-btn--ghost" onClick={handleCameraClose}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* Attachment-Chips */}
      {attachments.length > 0 && (
        <div className="attachment-chips">
          {attachments.map((att, i) => (
            <div key={i} className="attachment-chip">
              {att.previewUrl ? (
                <img src={att.previewUrl} alt={att.name} className="attachment-thumb" />
              ) : (
                <span className="attachment-typ">
                  {att.type === 'audio' ? 'Audio' : 'Datei'}
                </span>
              )}
              <span className="attachment-name">{att.name}</span>
              <button className="attachment-remove" onClick={() => removeAttachment(i)} title="Entfernen">×</button>
            </div>
          ))}
        </div>
      )}

      <form className="k-input-row" onSubmit={handleSubmit}>
        {/* Versteckter Datei-Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={DATEI_ACCEPT}
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {/* Media-Buttons */}
        <div className="chat-media-buttons">
          <button
            type="button"
            className="media-btn"
            title="Datei oder Bild anfügen"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || attachments.length >= 3}
          >
            Datei
          </button>
          <button
            type="button"
            className={`media-btn ${recording ? 'recording' : ''}`}
            title={recording ? 'Aufnahme stoppen' : 'Spracheingabe'}
            onClick={handleMicClick}
            disabled={disabled}
          >
            {recording ? 'Stopp' : 'Audio'}
          </button>
          <button
            type="button"
            className="media-btn"
            title="Foto aufnehmen"
            onClick={handleCameraOpen}
            disabled={disabled || attachments.length >= 3}
          >
            Foto
          </button>
        </div>

        <input
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            try { sessionStorage.setItem(ENTWURF_KEY, e.target.value); } catch {}
          }}
          onKeyDown={handleKeyDown}
          placeholder="Fragen Sie nach Zahlen, Beständen, Belegen …"
          disabled={disabled}
          className="k-input"
        />

        {disabled && onAbort ? (
          <button
            type="button"
            className="k-send k-send--stop"
            title="Anfrage abbrechen"
            onClick={onAbort}
          >
            ■ Stopp
          </button>
        ) : (
          <button
            type="submit"
            disabled={(!input.trim() && attachments.length === 0) || disabled}
            className="k-send"
          >
            Senden <span className="arr">→</span>
          </button>
        )}
      </form>
    </div>
  );
};

export default ChatInput;
